/**
 * Certificate conditions are typed in Admin with a small editor (Bold and
 * numbering), so they are saved as HTML. Conditions saved before the editor
 * are plain text, one condition per line. These helpers handle both.
 */

/** Tags the editor can produce. Everything else is dropped. */
const ALLOWED_TAGS = ["P", "BR", "STRONG", "B", "OL", "UL", "LI"];

/** True when the saved conditions come from the editor (HTML). */
export const isConditionsHtml = (value?: string | null) =>
  /^\s*<(p|ol|ul|strong|b)[\s>]/i.test(value ?? "");

const escapeHtml = (text: string) =>
  text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

/**
 * Turns old plain-text conditions into editor HTML, one paragraph per line,
 * so an existing condition opens in the editor with its lines kept.
 */
export const plainConditionsToHtml = (value?: string | null) => {
  if (!value) return "";
  if (isConditionsHtml(value)) return value;
  return value
    .split("\n")
    .filter((line) => line.trim())
    .map((line) => `<p>${escapeHtml(line)}</p>`)
    .join("");
};

/** True when the editor holds no text (it leaves "<p><br></p>" when cleared). */
export const isConditionsEmpty = (value?: string | null) =>
  !(value ?? "").replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim();

/**
 * Keeps only the editor's tags, without attributes, before the HTML is shown
 * on a review screen.
 */
export const sanitizeConditionsHtml = (value: string) => {
  const doc = new DOMParser().parseFromString(value, "text/html");
  const clean = (node: Element) => {
    Array.from(node.children).forEach((child) => {
      clean(child);
      if (!ALLOWED_TAGS.includes(child.tagName)) {
        child.replaceWith(...Array.from(child.childNodes));
        return;
      }
      Array.from(child.attributes).forEach((attr) =>
        child.removeAttribute(attr.name)
      );
    });
  };
  clean(doc.body);
  return doc.body.innerHTML;
};
